import { useState, useCallback, useMemo } from 'react'
import { ChevronDown, ChevronRight, FileText, Folder, FolderOpen } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchDocsTree, fetchFileContent } from '../store/docsSlice'
import type { DocTreeEntry } from '../store/docsSlice'
import './DocsSection.css'

interface DocsSectionProps {
  appId: string
}

interface TreeNode {
  name: string
  path: string
  type: 'blob' | 'tree'
  children: TreeNode[]
}

function buildTree(entries: DocTreeEntry[]): TreeNode[] {
  const root: TreeNode[] = []
  const map = new Map<string, TreeNode>()

  // Sort entries so directories come first, then alphabetically
  const sorted = [...entries].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'tree' ? -1 : 1
    return a.path.localeCompare(b.path)
  })

  for (const entry of sorted) {
    const parts = entry.path.split('/')
    const name = parts[parts.length - 1]
    const node: TreeNode = { name, path: entry.path, type: entry.type, children: [] }
    map.set(entry.path, node)

    if (parts.length === 1) {
      root.push(node)
    } else {
      const parentPath = parts.slice(0, -1).join('/')
      const parent = map.get(parentPath)
      if (parent) {
        parent.children.push(node)
      } else {
        root.push(node)
      }
    }
  }

  // Sort children: directories first, then files, alphabetically within each group
  function sortChildren(nodes: TreeNode[]) {
    nodes.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'tree' ? -1 : 1
      return a.name.localeCompare(b.name)
    })
    for (const node of nodes) {
      if (node.children.length > 0) sortChildren(node.children)
    }
  }
  sortChildren(root)

  return root
}

function FileTreeNode({
  node,
  depth,
  appId,
  expandedDirs,
  toggleDir,
  openFiles,
  onToggleFile,
  fileContents,
  fileLoading,
}: {
  node: TreeNode
  depth: number
  appId: string
  expandedDirs: Set<string>
  toggleDir: (path: string) => void
  openFiles: Set<string>
  onToggleFile: (path: string) => void
  fileContents: Record<string, string>
  fileLoading: Record<string, boolean>
}) {
  const isDir = node.type === 'tree'
  const isExpanded = expandedDirs.has(node.path)
  const isOpen = openFiles.has(node.path)

  return (
    <div className="docs-tree-node" data-testid="docs-tree-node">
      <button
        className={`docs-tree-node__row${isOpen ? ' docs-tree-node__row--selected' : ''}`}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
        onClick={() => {
          if (isDir) {
            toggleDir(node.path)
          } else {
            onToggleFile(node.path)
          }
        }}
        aria-expanded={!isDir ? isOpen : undefined}
        data-testid={isDir ? 'docs-tree-dir' : 'docs-tree-file'}
      >
        {isDir ? (
          <>
            {isExpanded ? (
              <ChevronDown size={14} className="docs-tree-node__chevron" aria-hidden="true" />
            ) : (
              <ChevronRight size={14} className="docs-tree-node__chevron" aria-hidden="true" />
            )}
            {isExpanded ? (
              <FolderOpen size={14} className="docs-tree-node__icon docs-tree-node__icon--dir" aria-hidden="true" />
            ) : (
              <Folder size={14} className="docs-tree-node__icon docs-tree-node__icon--dir" aria-hidden="true" />
            )}
          </>
        ) : (
          <>
            {isOpen ? (
              <ChevronDown size={14} className="docs-tree-node__chevron" aria-hidden="true" />
            ) : (
              <ChevronRight size={14} className="docs-tree-node__chevron" aria-hidden="true" />
            )}
            <FileText size={14} className="docs-tree-node__icon docs-tree-node__icon--file" aria-hidden="true" />
          </>
        )}
        <span className="docs-tree-node__name">{node.name}</span>
      </button>
      {!isDir && isOpen && (
        <div className="docs-tree-node__inline-content" style={{ marginLeft: `${12 + depth * 16}px` }}>
          {fileLoading[node.path] ? (
            <div className="docs-section__file-loading" role="status">
              Loading file...
            </div>
          ) : fileContents[node.path] !== undefined && fileContents[node.path] !== null ? (
            <pre className="docs-section__file-content" data-testid="docs-file-content">
              <code>{fileContents[node.path]}</code>
            </pre>
          ) : null}
        </div>
      )}
      {isDir && isExpanded && node.children.length > 0 && (
        <div className="docs-tree-node__children">
          {node.children.map((child) => (
            <FileTreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              appId={appId}
              expandedDirs={expandedDirs}
              toggleDir={toggleDir}
              openFiles={openFiles}
              onToggleFile={onToggleFile}
              fileContents={fileContents}
              fileLoading={fileLoading}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DocsSection({ appId }: DocsSectionProps) {
  const dispatch = useAppDispatch()
  const { tree, loading, error } = useAppSelector((state) => state.docs)
  const fileContents = useAppSelector((state) => state.docs.fileContents)
  const fileLoading = useAppSelector((state) => state.docs.fileLoading)

  const [expanded, setExpanded] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set())
  const [openFiles, setOpenFiles] = useState<Set<string>>(new Set())

  const treeNodes = useMemo(() => buildTree(tree), [tree])

  const handleToggleExpand = useCallback(() => {
    const next = !expanded
    setExpanded(next)
    if (next && !hasFetched) {
      setHasFetched(true)
      dispatch(fetchDocsTree(appId))
    } else if (next && hasFetched) {
      // Re-sync on every expand
      dispatch(fetchDocsTree(appId))
    }
  }, [expanded, hasFetched, dispatch, appId])

  const toggleDir = useCallback((path: string) => {
    setExpandedDirs((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }, [])

  const handleToggleFile = useCallback((path: string) => {
    setOpenFiles((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
        if (!fileContents[path] && !fileLoading[path]) {
          dispatch(fetchFileContent({ appId, path }))
        }
      }
      return next
    })
  }, [dispatch, appId, fileContents, fileLoading])

  return (
    <section className="docs-section" data-testid="docs-section" aria-labelledby="docs-section-heading">
      <button
        className="docs-section__header"
        onClick={handleToggleExpand}
        aria-expanded={expanded}
        data-testid="docs-section-toggle"
      >
        {expanded ? (
          <ChevronDown size={16} className="docs-section__chevron" aria-hidden="true" />
        ) : (
          <ChevronRight size={16} className="docs-section__chevron" aria-hidden="true" />
        )}
        <h2 className="docs-section__title" id="docs-section-heading">Docs</h2>
      </button>

      {expanded && (
        <div className="docs-section__body" data-testid="docs-section-body">
          {loading && tree.length === 0 ? (
            <div className="docs-section__loading" data-testid="docs-section-loading" role="status">
              Syncing docs...
            </div>
          ) : error ? (
            <div className="docs-section__error" data-testid="docs-section-error" role="alert">
              {error}
            </div>
          ) : tree.length === 0 ? (
            <div className="docs-section__empty" data-testid="docs-section-empty">
              No documentation files found.
            </div>
          ) : (
            <div className="docs-section__content">
              <div className="docs-section__tree" data-testid="docs-tree">
                {treeNodes.map((node) => (
                  <FileTreeNode
                    key={node.path}
                    node={node}
                    depth={0}
                    appId={appId}
                    expandedDirs={expandedDirs}
                    toggleDir={toggleDir}
                    openFiles={openFiles}
                    onToggleFile={handleToggleFile}
                    fileContents={fileContents}
                    fileLoading={fileLoading}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}

export default DocsSection
